'use client'

import React, { useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { Diskette } from 'solar-icons'
import { toast } from 'sonner'
import { saveOrder } from '@/lib/actions'
import { useCart, useCheckout, useSearchParams } from '@/lib/hooks'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Textarea,
} from '@/components/ui'

export const SaveOrder = () => {
  const [isSavingOrder, startSavingOrder] = useTransition()
  const { cartId } = useSearchParams()
  const { resetCart, cart } = useCart()
  const { resetCheckout } = useCheckout()
  const router = useRouter()

  const handleSaveOrder = (data: FormData) => {
    const reference = data.get('reference') as string
    startSavingOrder(async () => {
      try {
        await saveOrder({ cartId, reference })
        resetCheckout()
        resetCart()
        toast.success('Order saved successfully.')
        router.push('/')
      } catch (error) {
        toast.error('Failed to save order. Please try again.')
      }
    })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full shrink-0 gap-2 lg:w-[160px]"
          size="lg"
        >
          <Diskette strokeWidth={1.2} className="text-gray-950" />
          <span>Save Order</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save order</DialogTitle>
          <DialogDescription>
            Please enter a reference for this order to identify it later.
          </DialogDescription>
        </DialogHeader>

        <form action={handleSaveOrder} className="flex w-full flex-col gap-4">
          <Textarea
            autoFocus
            name="reference"
            placeholder="E.g. John Doe's order"
            className="min-h-[100px]"
            disabled={isSavingOrder}
            defaultValue={cart.cart?.reference}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                size="lg"
                disabled={isSavingOrder}
              >
                Cancel
              </Button>
            </DialogClose>

            <Button
              type="submit"
              variant="default"
              className="w-full"
              size="lg"
              disabled={isSavingOrder}
            >
              {isSavingOrder ? 'Saving...' : 'Save Order'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
